import React, { useState } from "react";

import { useParams } from "react-router-dom";

import { Container, Icon } from "@mui/material";

import {
  Button,
  Card,
  CopyButton,
  Loader,
  Modal,
  Pagination,
  Table,
  Tabs,
  Text,
  TextProps,
  Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { GridRenderCellParams } from "@mui/x-data-grid";
import dayjs from "dayjs";
import DatagridDisplay from "../../Components/DatagridDisplay";
import MegaLoader from "../../Components/MegaLoader";
import { BrandImages, Colors } from "../../Lib/Data";
import { Endpoints } from "../../Lib/Endpoints";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
  generateRandomString,
  getFinancialValueFromNumeric,
  getPaginationTotal,
} from "../../Lib/Methods";
import { NonPaginatedResponse, PaginatedResponse } from "../../Lib/Responses";
import {
  PaginationProps,
  TransactionProps,
  UserProfileProps,
  UserSubscriptionProps,
} from "../../Lib/Types";
import { PerformRequest, usePerformRequest } from "../../Lib/usePerformRequest";
import "./styles.scss";
import { IconBroadcast, IconCurrencyNaira } from "@tabler/icons-react";
const SingleUser: React.FC = () => {
  const { userID } = useParams();
  const {
    isLoading: isLoadingUser,
    reloadData: reloadUser,
    data: user,
  } = usePerformRequest<NonPaginatedResponse<UserProfileProps>>({
    method: "GET",
    route: Endpoints.GetUserProfile(userID as string),
  });

  const [theme, setTheme] = useState<"dark" | "light">("light");

  const [currentTransaction, setCurrentTransaction] =
    useState<TransactionProps>();
  const [currentSubscription, setCurrentSubscription] =
    useState<UserSubscriptionProps>();
  const [
    isTransactionModalOpened,
    { open: OpenTransactionModal, close: CloseTransactionModal },
  ] = useDisclosure(false);
  const [
    isSubscriptionModalOpened,
    { open: OpenSubscriptionModal, close: CloseSubscriptionModal },
  ] = useDisclosure(false);

  const [pagination, setPagination] = useState<PaginationProps>({
    page: 1,
    limit: 20,
  });
  const {
    data: transactions,
    reloadData: reloadTransactions,
    isLoading: isLoadingTransactions,
  } = usePerformRequest<NonPaginatedResponse<TransactionProps[]>>({
    method: "GET",
    route: Endpoints.GetTransactionsByUserID(userID as string),
  });
  const {
    data: subscriptions,
    reloadData: reloadSubscriptions,
    isLoading: isLoadingSubscriptions,
  } = usePerformRequest<PaginatedResponse<UserSubscriptionProps[]>>({
    method: "GET",
    route: Endpoints.GetSubscriptionsByUserID(
      pagination.page,
      pagination.limit,
      userID as string
    ),
  });

  const [isRemovingUserFromPlan, setRemovingUserFromPlan] = useState(false);
  const RemoveUserFromPlan = async (subscriptionID: string) => {
    setRemovingUserFromPlan(true);
    const r = await PerformRequest({
      method: "DELETE",
      route: Endpoints.GetRemoveUserFromPlan(subscriptionID),
      callback: () => {
        setRemovingUserFromPlan(false);
      },
    });
    setRemovingUserFromPlan(false);
    if (r.status === 200) {
      reloadSubscriptions();
      DefaultSuccessNotification("Subscription expired successfully");
    } else {
      DefaultErrorNotification(r.message);
    }
  };
  const profileCardLabelProps: TextProps = {
    fz: "sm",
    c: theme === "dark" ? Colors.WhiteBlueTransparent : Colors.GraySecondary,
    fw: 500,
  };
  const profileCardValueProps: TextProps = {
    fz: "sm",
    c: theme === "dark" ? Colors.GreenPrimary : Colors.GreenSecondary,
    fw: 500,
    ml: 5,
  };
  return (
    <Container>
      <div className="single-user-container">
        {isLoadingUser ? (
          <div className="loader flex-row align-center justify-center">
            <MegaLoader />
          </div>
        ) : (
          <>
            <Card
              maw={400}
              withBorder
              className={`profile-card profile-card-${theme}`}
            >
              <div className="flex-row align-center justify-between">
                <div className="initials flex-row align-center justify-center">
                  <Text className="text">{user?.data?.name[0]}</Text>
                </div>
              </div>
              <div className="row flex-row align-center">
                <Text {...profileCardLabelProps}>Name:</Text>

                <Text {...profileCardValueProps}>{user?.data?.name}</Text>
              </div>
              <div className="row flex-row align-center">
                <Text {...profileCardLabelProps}>Email:</Text>

                <Text {...profileCardValueProps}>{user?.data?.email}</Text>
              </div>
              <div className="row flex-row align-center">
                <Text {...profileCardLabelProps}>Date Joined:</Text>

                <Text {...profileCardValueProps}>
                  {dayjs(user?.data?.createdAt ?? "").format(
                    "ddd, DD MMM YYYY"
                  )}
                </Text>
              </div>
              <div className="row flex-row align-center">
                <Text {...profileCardLabelProps}>Is Promoter:</Text>

                <Text
                  {...profileCardValueProps}
                  c={
                    user?.data?.isPromoter
                      ? Colors.GreenPrimary
                      : Colors.RedPrimary
                  }
                >
                  {user?.data?.isPromoter ? "Yes" : "No"}
                </Text>
              </div>
              <div className="row flex-row align-center">
                <Text {...profileCardLabelProps}>Is Verified:</Text>

                <Text
                  {...profileCardValueProps}
                  c={
                    user?.data?.isVerified
                      ? Colors.GreenPrimary
                      : Colors.RedPrimary
                  }
                >
                  {user?.data?.isVerified ? "Yes" : "No"}
                </Text>
              </div>
              <div className="row flex-row align-center">
                <Text {...profileCardLabelProps}>Gender:</Text>

                <Text {...profileCardValueProps} tt="capitalize">
                  {user?.data?.gender ?? "Not Specified"}
                </Text>
              </div>
            </Card>
            <Tabs mt={20} defaultValue="transactions">
              <Tabs.List grow>
                <Tabs.Tab
                  value="transactions"
                  leftSection={<IconCurrencyNaira size={17} />}
                >
                  Transactions
                </Tabs.Tab>
                <Tabs.Tab
                  value="subscriptions"
                  leftSection={<IconBroadcast size={17} />}
                >
                  Subscriptions
                </Tabs.Tab>
              </Tabs.List>
              <Tabs.Panel value="transactions">
                <DatagridDisplay
                  sx={{
                    marginTop: 3,
                  }}
                  rows={transactions?.data ?? []}
                  onRowClick={(params) => {
                    OpenTransactionModal();
                    setCurrentTransaction(params.row);
                  }}
                  isLoading={isLoadingTransactions}
                  columns={[
                    {
                      field: "createdAt",
                      width: 210,
                      headerName: "Date",
                      renderCell: (
                        params: GridRenderCellParams<TransactionProps>
                      ) => {
                        return (
                          <span>
                            {dayjs(params.row.createdAt).format("DD MMM YYYY")}
                            &nbsp;at&nbsp;
                            {dayjs(params.row.createdAt).format("hh:mmA")}
                          </span>
                        );
                      },
                    },
                    {
                      field: "id",
                      width: 320,

                      headerName: "Transaction ID",
                      renderCell: (
                        params: GridRenderCellParams<TransactionProps>
                      ) => {
                        return (
                          <CopyButton value={params.row.id}>
                            {({ copied, copy }) => (
                              <Tooltip
                                label={copied ? "Copied!" : "Copy to clipboard"}
                              >
                                <span
                                  className="pointer"
                                  onClick={copy}
                                  style={{
                                    color: copied
                                      ? Colors.GreenPrimary
                                      : Colors.BluePrimary,
                                  }}
                                >
                                  {params.row.id}
                                </span>
                              </Tooltip>
                            )}
                          </CopyButton>
                        );
                      },
                    },
                    {
                      field: "reference",
                      width: 130,

                      headerName: "Reference",
                      renderCell: (
                        params: GridRenderCellParams<TransactionProps>
                      ) => {
                        return (
                          <CopyButton value={params.row.reference}>
                            {({ copied, copy }) => (
                              <Tooltip
                                label={copied ? "Copied!" : "Copy to clipboard"}
                              >
                                <span
                                  className="pointer"
                                  onClick={copy}
                                  style={{
                                    color: copied
                                      ? Colors.GreenPrimary
                                      : Colors.BluePrimary,
                                  }}
                                >
                                  {params.row.reference}
                                </span>
                              </Tooltip>
                            )}
                          </CopyButton>
                        );
                      },
                    },
                    {
                      field: "amount",
                      width: 140,

                      headerName: "Amount",
                      renderCell: (
                        params: GridRenderCellParams<TransactionProps>
                      ) => {
                        return (
                          <span className="amount">
                            ₦{getFinancialValueFromNumeric(params.row.amount)}
                          </span>
                        );
                      },
                    },
                    {
                      field: "duration",

                      headerName: "Duration",
                      renderCell: (
                        params: GridRenderCellParams<TransactionProps>
                      ) => {
                        return <span>{params.row.duration}</span>;
                      },
                    },
                    {
                      field: "services",
                      width: 210,

                      headerName: "Services",
                      renderCell: (
                        params: GridRenderCellParams<TransactionProps>
                      ) => {
                        return (
                          <div className="flex-row align-center service-images">
                            {params.row.services.map((service) => {
                              return (
                                <img
                                  src={BrandImages.retrieveImage(service)}
                                  alt=""
                                  className="image"
                                />
                              );
                            })}
                          </div>
                        );
                      },
                    },
                  ]}
                />
                {currentTransaction && (
                  <Modal
                    opened={isTransactionModalOpened}
                    onClose={() => {
                      CloseTransactionModal();
                      setCurrentTransaction(undefined);
                    }}
                    title="Transaction Details"
                    centered
                    size="lg"
                  >
                    <Table variant="vertical" layout="fixed" withTableBorder>
                      <Table.Thead>
                        <Table.Tr key={generateRandomString(12)}>
                          <Table.Th w={200}>Transaction ID</Table.Th>
                          <Table.Td>
                            <CopyButton value={currentTransaction.id}>
                              {({ copied, copy }) => (
                                <Tooltip
                                  label={
                                    copied ? "Copied!" : "Copy to clipboard"
                                  }
                                >
                                  <div
                                    className="pointer flex-row align-center"
                                    onClick={copy}
                                    style={{
                                      color: copied
                                        ? Colors.GreenPrimary
                                        : Colors.BluePrimary,
                                      height: "100%",
                                    }}
                                  >
                                    {currentTransaction.id}
                                  </div>
                                </Tooltip>
                              )}
                            </CopyButton>
                          </Table.Td>
                        </Table.Tr>
                        <Table.Tr key={generateRandomString(12)}>
                          <Table.Th w={200}>Reference</Table.Th>
                          <Table.Td>
                            <CopyButton value={currentTransaction.reference}>
                              {({ copied, copy }) => (
                                <Tooltip
                                  label={
                                    copied ? "Copied!" : "Copy to clipboard"
                                  }
                                >
                                  <div
                                    className="pointer flex-row align-center"
                                    onClick={copy}
                                    style={{
                                      color: copied
                                        ? Colors.GreenPrimary
                                        : Colors.BluePrimary,
                                      height: "100%",
                                    }}
                                  >
                                    {currentTransaction.reference}
                                  </div>
                                </Tooltip>
                              )}
                            </CopyButton>
                          </Table.Td>
                        </Table.Tr>
                        <Table.Tr key={generateRandomString(12)}>
                          <Table.Th>Date</Table.Th>
                          <Table.Td>
                            {dayjs(currentTransaction.createdAt).format(
                              "DD MMM YYYY - hh:mmA"
                            )}
                          </Table.Td>
                        </Table.Tr>
                        <Table.Tr key={generateRandomString(12)}>
                          <Table.Th>Service</Table.Th>
                          <Table.Td>
                            <div className="flex-row align-center temp-service-images">
                              {currentTransaction.services.map((service) => {
                                return (
                                  <img
                                    src={BrandImages.retrieveImage(service)}
                                    alt=""
                                    className="image"
                                  />
                                );
                              })}
                            </div>
                          </Table.Td>
                        </Table.Tr>
                        <Table.Tr key={generateRandomString(12)}>
                          <Table.Th>Amount</Table.Th>
                          <Table.Td fw={600} c={Colors.GreenSecondary}>
                            ₦{currentTransaction.amount}
                          </Table.Td>
                        </Table.Tr>
                        <Table.Tr key={generateRandomString(12)}>
                          <Table.Th>User</Table.Th>
                          <Table.Td>
                            {user?.data?.id ?? <Loader size={15} />}
                          </Table.Td>
                        </Table.Tr>
                        {currentTransaction.coupon && (
                          <Table.Tr key={generateRandomString(12)}>
                            <Table.Th>Coupon Code</Table.Th>
                            <Table.Td>
                              {currentTransaction.coupon.code}
                            </Table.Td>
                          </Table.Tr>
                        )}
                        {currentTransaction.coupon && (
                          <Table.Tr key={generateRandomString(12)}>
                            <Table.Th>Coupon Discount</Table.Th>
                            <Table.Td fw={600} c={Colors.GreenSecondary}>
                              {currentTransaction.coupon.discount}%
                            </Table.Td>
                          </Table.Tr>
                        )}
                      </Table.Thead>
                    </Table>
                  </Modal>
                )}
              </Tabs.Panel>
              <Tabs.Panel value="subscriptions">
                <DatagridDisplay
                  sx={{
                    marginTop: 3,
                  }}
                  rows={subscriptions?.data ?? []}
                  onRowClick={(params) => {
                    OpenSubscriptionModal();
                    setCurrentSubscription(params.row);
                  }}
                  isLoading={isLoadingSubscriptions}
                  columns={[
                    {
                      field: "createdAt",
                      width: 210,
                      headerName: "Initial Subscription Date",
                      renderCell: (
                        params: GridRenderCellParams<UserSubscriptionProps>
                      ) => {
                        return (
                          <span>
                            {dayjs(params.row.createdAt).format("DD MMM YYYY")}
                            &nbsp;at&nbsp;
                            {dayjs(params.row.createdAt).format("hh:mmA")}
                          </span>
                        );
                      },
                    },
                    {
                      field: "lastSubscriptionDate",
                      width: 210,
                      headerName: "Last Subscription Date",
                      renderCell: (
                        params: GridRenderCellParams<UserSubscriptionProps>
                      ) => {
                        return (
                          <span>
                            {dayjs(params.row.lastSubscriptionDate).format(
                              "DD MMM YYYY"
                            )}
                            &nbsp;at&nbsp;
                            {dayjs(params.row.lastSubscriptionDate).format(
                              "hh:mmA"
                            )}
                          </span>
                        );
                      },
                    },
                    {
                      field: "expiresAt",
                      width: 210,
                      headerName: "Expiration",
                      renderCell: (
                        params: GridRenderCellParams<UserSubscriptionProps>
                      ) => {
                        return (
                          <span>
                            {dayjs(params.row.expiresAt).format("DD MMM YYYY")}
                            &nbsp;at&nbsp;
                            {dayjs(params.row.expiresAt).format("hh:mmA")}
                          </span>
                        );
                      },
                    },
                    {
                      field: "id",
                      width: 320,

                      headerName: "Transaction ID",
                      renderCell: (
                        params: GridRenderCellParams<UserSubscriptionProps>
                      ) => {
                        return (
                          <CopyButton value={params.row.transactionID}>
                            {({ copied, copy }) => (
                              <Tooltip
                                label={copied ? "Copied!" : "Copy to clipboard"}
                              >
                                <span
                                  className="pointer"
                                  onClick={copy}
                                  style={{
                                    color: copied
                                      ? Colors.GreenPrimary
                                      : Colors.BluePrimary,
                                  }}
                                >
                                  {params.row.transactionID}
                                </span>
                              </Tooltip>
                            )}
                          </CopyButton>
                        );
                      },
                    },
                    {
                      field: "planID",
                      width: 320,

                      headerName: "Plan ID",
                      renderCell: (
                        params: GridRenderCellParams<UserSubscriptionProps>
                      ) => {
                        return (
                          <CopyButton value={params.row.planID}>
                            {({ copied, copy }) => (
                              <Tooltip
                                label={copied ? "Copied!" : "Copy to clipboard"}
                              >
                                <span
                                  className="pointer"
                                  onClick={copy}
                                  style={{
                                    color: copied
                                      ? Colors.GreenPrimary
                                      : Colors.BluePrimary,
                                  }}
                                >
                                  {params.row.planID}
                                </span>
                              </Tooltip>
                            )}
                          </CopyButton>
                        );
                      },
                    },
                    {
                      field: "service",
                      width: 210,

                      headerName: "Services",
                      renderCell: (
                        params: GridRenderCellParams<UserSubscriptionProps>
                      ) => {
                        return (
                          <div className="flex-row align-center service-images">
                            <img
                              src={BrandImages.retrieveImage(
                                params.row.service
                              )}
                              alt=""
                              className="image"
                            />
                          </div>
                        );
                      },
                    },
                  ]}
                />
                {currentSubscription && (
                  <Modal
                    opened={isSubscriptionModalOpened}
                    onClose={() => {
                      CloseSubscriptionModal();
                      setCurrentSubscription(undefined);
                    }}
                    title="Subscription Details"
                    centered
                    size="lg"
                  >
                    <Table variant="vertical" layout="fixed" withTableBorder>
                      <Table.Thead>
                        <Table.Tr key={generateRandomString(12)}>
                          <Table.Th w={200}>Subscription ID</Table.Th>
                          <Table.Td>
                            <CopyButton value={currentSubscription.id}>
                              {({ copied, copy }) => (
                                <Tooltip
                                  label={
                                    copied ? "Copied!" : "Copy to clipboard"
                                  }
                                >
                                  <div
                                    className="pointer flex-row align-center"
                                    onClick={copy}
                                    style={{
                                      color: copied
                                        ? Colors.GreenPrimary
                                        : Colors.BluePrimary,
                                      height: "100%",
                                    }}
                                  >
                                    {currentSubscription.id}
                                  </div>
                                </Tooltip>
                              )}
                            </CopyButton>
                          </Table.Td>
                        </Table.Tr>
                        <Table.Tr key={generateRandomString(12)}>
                          <Table.Th w={200}>Transaction ID</Table.Th>
                          <Table.Td>
                            <CopyButton
                              value={currentSubscription.transactionID}
                            >
                              {({ copied, copy }) => (
                                <Tooltip
                                  label={
                                    copied ? "Copied!" : "Copy to clipboard"
                                  }
                                >
                                  <div
                                    className="pointer flex-row align-center"
                                    onClick={copy}
                                    style={{
                                      color: copied
                                        ? Colors.GreenPrimary
                                        : Colors.BluePrimary,
                                      height: "100%",
                                    }}
                                  >
                                    {currentSubscription.transactionID}
                                  </div>
                                </Tooltip>
                              )}
                            </CopyButton>
                          </Table.Td>
                        </Table.Tr>
                        <Table.Tr key={generateRandomString(12)}>
                          <Table.Th>Initial Subscription Date</Table.Th>
                          <Table.Td>
                            {dayjs(currentSubscription.createdAt).format(
                              "DD MMM YYYY - hh:mmA"
                            )}
                          </Table.Td>
                        </Table.Tr>
                        <Table.Tr key={generateRandomString(12)}>
                          <Table.Th>Last Subscription Date</Table.Th>
                          <Table.Td>
                            {dayjs(
                              currentSubscription.lastSubscriptionDate
                            ).format("DD MMM YYYY - hh:mmA")}
                          </Table.Td>
                        </Table.Tr>
                        <Table.Tr key={generateRandomString(12)}>
                          <Table.Th>Expiration Date</Table.Th>
                          <Table.Td>
                            {dayjs(currentSubscription.expiresAt).format(
                              "DD MMM YYYY - hh:mmA"
                            )}
                          </Table.Td>
                        </Table.Tr>
                        <Table.Tr key={generateRandomString(12)}>
                          <Table.Th>Service</Table.Th>
                          <Table.Td>
                            <div className="flex-row align-center temp-service-images">
                              <img
                                src={BrandImages.retrieveImage(
                                  currentSubscription.service
                                )}
                                alt=""
                                className="image"
                              />
                            </div>
                          </Table.Td>
                        </Table.Tr>
                      </Table.Thead>
                    </Table>
                    <Button
                      loading={isRemovingUserFromPlan}
                      color={Colors.RedPrimary}
                      mt={10}
                      onClick={() => {
                        RemoveUserFromPlan(currentSubscription.id);
                      }}
                    >
                      Expire Subscription
                    </Button>
                  </Modal>
                )}
                <div className="flex-row align-center justify-center width-100">
                  <Pagination
                    value={pagination.page}
                    onChange={(value) => {
                      setPagination({ ...pagination, page: value });
                    }}
                    my={40}
                    disabled={
                      subscriptions?.data.length === 0 || isLoadingSubscriptions
                    }
                    total={getPaginationTotal(
                      subscriptions?.totalRecords,
                      pagination.limit
                    )}
                  />
                </div>
              </Tabs.Panel>
            </Tabs>
          </>
        )}
      </div>
    </Container>
  );
};

export default SingleUser;
